<template>
  <div>
    <v-col>
      <v-row class="ma-0">
        <h2 class="pl-2 pa-sm-0 primary--text" v-if="showLogin">
          {{ $translate('LOGIN_OR_CONTINUE') }}
        </h2>
        <h2 class="pl-2 pa-sm-0 primary--text" v-else>
          {{ $translate('CREATE_ACCOUNT_OR_CONTINUE') }}
        </h2>
      </v-row>
      <v-row class="ma-0">
        <v-col v-if="showLogin">
          <v-form ref="loginForm">
            <v-col class="pl-0 pb-0">
              <h4>{{ $translate('LOGIN') }}</h4>
            </v-col>
            <v-alert
              v-if="alertText != null && alertText !== ''"
              type="info"
              dense
              class="mt-3 mb-6"
            >
              {{ alertText }}
            </v-alert>
            <v-text-field
              v-model="email"
              outlined
              dense
              disabled
              :label="$translate('EMAIL')"
            />
            <v-text-field
              v-model="password"
              outlined
              dense
              :label="$translate('PASSWORD')"
              :type="hidePassword ? 'password' : 'text'"
              :append-icon="hidePassword ? 'visibility' : 'visibility_off'"
              @click:append="() => (hidePassword = !hidePassword)"
            />
            <v-btn
              block
              color="primary"
              class="primary-font-color"
              @click="login()"
              :loading="loginLoading"
            >
              {{ $translate('LOGIN') }}
            </v-btn>
          </v-form>
        </v-col>
        <v-col v-else>
          <v-form ref="registerForm">
            <v-col class="pl-0">
              <h4>{{ $translate('CREATE_ACCOUNT') }}</h4>
            </v-col>
            <v-alert
              v-if="alertText != null && alertText !== ''"
              type="info"
              dense
              class="mt-3 mb-6"
            >
              {{ alertText }}
            </v-alert>
            <v-text-field
              v-model="email"
              disabled
              outlined
              dense
              :label="$translate('EMAIL')"
            />
            <v-text-field
              v-model="password"
              outlined
              dense
              :rules="passwordRules"
              :label="$translate('PASSWORD')"
              :type="hidePassword ? 'password' : 'text'"
              :append-icon="hidePassword ? 'visibility' : 'visibility_off'"
              @click:append="() => (hidePassword = !hidePassword)"
            />
            <v-text-field
              v-model="confirmPassword"
              outlined
              dense
              :rules="passwordConfirmRules"
              :label="$translate('CONFIRM_PASS')"
              :type="hidePassword ? 'password' : 'text'"
              :append-icon="hidePassword ? 'visibility' : 'visibility_off'"
              @click:append="() => (hidePassword = !hidePassword)"
            />
            <v-btn
              block
              color="primary"
              class="primary-font-color"
              @click="register()"
              :loading="registerLoading"
            >
              {{ $translate('REGISTER') }}
            </v-btn>
            <h4 class="pt-3" v-if="memberBenefits.length !== 0">
              {{ $translate('BENEFITS_REGISTERING') }}
            </h4>
            <v-col v-for="item in memberBenefits" :key="item" class="pa-0 ma-0">
              <v-row>
                <v-col cols="1">
                  <v-icon color="success">mdi-check-circle</v-icon>
                </v-col>
                <v-col>
                  <strong>{{ item }}</strong>
                </v-col>
              </v-row>
            </v-col>
          </v-form>
        </v-col>
        <v-divider vertical class="hidden-sm-and-down"></v-divider>
        <v-col>
          <v-divider class="hidden-sm-and-up"></v-divider>
          <v-col class="pl-0">
            <h4>{{ $translate('CONTINUE_AS_GUEST') }}</h4>
          </v-col>
          <v-btn
            block
            color="primary"
            class="primary-font-color"
            @click="continueOrder()"
          >
            {{ $translate('CONTINUE_AS_GUEST') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
    <v-row class="mt-7">
      <v-btn color="secondary" dark class="white--text" @click="goBack">
        {{ $translate('BACK') }}
      </v-btn>
    </v-row>
  </div>
</template>

<script>
import doRequest from '@/api/dssRequest';
import UserObject from '@/objects/UserObject';

export default {
  name: 'Booking_Order_Account',
  data() {
    return {
      userHasPartnerGroupAccount: false,
      hidePassword: true,
      userHasAccount: false,
      password: '',
      registerLoading: false,
      loginLoading: false,
      confirmPassword: '',
      email: '',
      productShoppingCart: {},
      memberBenefits: [],
      passwordRules: [
        (v) => !!v || this.$translate('PASSWORD_REQUIRED'),
        (v) =>
          (v.length >= 8 && v.length <= 48) ||
          this.$translate('PASS_LENGTH_LIMIT'),
      ],
      passwordConfirmRules: [
        (v) =>
          (!!v && v === this.password) ||
          this.$translate('REGISTER_PASS_NOT_MATCH'),
      ],
      userState: null,
    };
  },
  computed: {
    showLogin() {
      if (this.userHasAccount || this.userHasPartnerGroupAccount) {
        switch (this.userState) {
          case 1: // Registered, not activated
          case 2: // Registered and activated
            return true;
          default: // Imported / Interested / Booking made without registration
            return false;
        }
      }

      return false;
    },
    alertText() {
      if (this.userHasAccount || this.userHasPartnerGroupAccount) {
        switch (this.userState) {
          case 1: // Registered, not activated
            return this.$translate('ORDER_ACCOUNT_NOT_ACTIVATED');
          case 2: // Registered and activated
            return this.$translate('FOUND_AN_ACCOUNT');
          default: // Imported / Interested / Booking made without registration
            return null;
        }
      }

      return null;
    },
  },
  mounted() {
    this.email = this.$store.getters['auth/tmpEmail'];
    this.checkEmail();
    this.init();
    this.productShoppingCart = this.$store.getters[
      'shoppingCart/orders'
    ].filter((item) => item.ShoppingCartType === 'Product');
  },
  methods: {
    init() {
      const pgDetails = this.$store.getters['Account/partnerGroupDetails'];
      if (pgDetails.MemberBenefit1) {
        this.memberBenefits.push(pgDetails.MemberBenefit1);
      }
      if (pgDetails.MemberBenefit2) {
        this.memberBenefits.push(pgDetails.MemberBenefit2);
      }
      if (pgDetails.MemberBenefit3) {
        this.memberBenefits.push(pgDetails.MemberBenefit3);
      }
      if (pgDetails.MemberBenefit4) {
        this.memberBenefits.push(pgDetails.MemberBenefit4);
      }
    },
    continueOrder() {
      this.$store.commit('auth/setTmpEmail', this.email);
      this.$emit('continue');
    },
    goBack() {
      this.$emit('back');
    },
    register() {
      if (!this.$refs.registerForm.validate()) {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        return;
      }
      this.registerLoading = true;
      this.errorMessage = '';
      this.user = new UserObject(this.email);

      this.user.userRegister(
        this.password,
        () => {
          window.EventBus.fire('triggerOpenAccountFromMenu', {
            toggle: true,
            component: 'RegisterSuccess',
          });
          this.registerLoading = false;
          this.continueOrder();
        },
        (error) => {
          this.registerLoading = false;
          this.errorMessage = this.$translateError(error.data.ErrorCode);
        }
      );
    },
    login() {
      if (!this.$refs.loginForm.validate()) {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        return;
      }
      this.user = new UserObject(this.email);
      this.loginLoading = true;

      this.user.userlogin(
        this.password,
        (response) => {
          this.loggedSuccess(response);
        },
        this.loggedUnsuccessfull
      );
    },
    loggedSuccess() {
      this.loginLoading = false;
      window.EventBus.fire('activateSnackBar', {
        text: this.$translate('SUCCESS_LOGGED_IN'),
        color: 'error',
      });
      this.continueOrder();
    },
    loggedUnsuccessfull(error) {
      this.loginLoading = false;
      window.EventBus.fire('activateSnackBar', {
        text: this.$translateError(error.data.ErrorCode),
        color: 'error',
      });
    },
    checkEmail() {
      const payload = {
        Email: this.email,
      };
      doRequest(
        (response) => {
          this.userHasAccount = response.data.AlreadyPartOfPartnerGroup;
          this.userState = response.data.UserState;
          if(response.data.Partnergroups != 0){
            this.userHasPartnerGroupAccount = true;          }
        },
        () => {},
        'checkEmail',
        `${this.$store.getters['Account/partnerGroupDetails'].AccountId}_UM`,
        payload
      );
    },
  },
};
</script>
